import * as TSL from 'three/tsl';
import { MeshStandardNodeMaterial } from 'three/webgpu';

import { EngineClass } from '../../../systems/ClassRegistry.js';
import { property } from '../../../utils/serialization/decorator.js';
import { ISerializableObjectTag } from '../../../utils/serialization/serializable.js';

import { NodeMaterialAsset } from './NodeMaterialAsset.js';

import type { IDumper, ILoader } from '../../../utils/serialization/factories.js';

/** Sinusoidal vertex displacement on `MeshStandardNodeMaterial` for simple procedural animation. */
@EngineClass('VertexWaveNodeMaterialAsset', {
  isNodeMaterialAsset: true,
  nodeMaterialDisplayName: 'Vertex Wave Displacement',
  nodeMaterialGroup: 'TSL Node FX',
})
export class VertexWaveNodeMaterialAsset extends NodeMaterialAsset(MeshStandardNodeMaterial) {
  readonly [ISerializableObjectTag] = true as const;

  @property({ type: 'number', min: 0.1, max: 20.0, step: 0.1, description: 'Wave frequency along X' })
    frequency = 4.0;

  @property({ type: 'number', min: 0.001, max: 1.0, step: 0.001, description: 'Wave amplitude' })
    amplitude = 0.15;

  @property({ type: 'number', min: 0.01, max: 10.0, step: 0.01, description: 'Animation speed' })
    speed = 2.0;

  @property({ type: 'number', min: 0.0, max: 1.0, step: 0.01, description: 'Roughness' })
  override roughness = 0.4;

  @property({ type: 'number', min: 0.0, max: 1.0, step: 0.01, description: 'Metalness' })
  override metalness = 0.1;

  private readonly _roughnessUniform = TSL.uniform(this.roughness);
  private readonly _metalnessUniform = TSL.uniform(this.metalness);
  private readonly _frequencyUniform = TSL.uniform(this.frequency);
  private readonly _amplitudeUniform = TSL.uniform(this.amplitude);
  private readonly _speedUniform = TSL.uniform(this.speed);

  constructor() {
    super();
    this.rebuild();
  }

  override rebuild(): void {
    this.applyCommonMaterialState();
    this.frequency = this.clampFiniteNumber(this.frequency, 0.1, 20.0, 4.0);
    this.amplitude = this.clampFiniteNumber(this.amplitude, 0.001, 1.0, 0.15);
    this.speed = this.clampFiniteNumber(this.speed, 0.01, 10.0, 2.0);
    this.roughness = this.clampFiniteNumber(this.roughness, 0.0, 1.0, 0.4);
    this.metalness = this.clampFiniteNumber(this.metalness, 0.0, 1.0, 0.1);
    this._roughnessUniform.value = this.roughness;
    this._metalnessUniform.value = this.metalness;
    this._frequencyUniform.value = this.frequency;
    this._amplitudeUniform.value = this.amplitude;
    this._speedUniform.value = this.speed;
    this.roughnessNode = this._roughnessUniform;
    this.metalnessNode = this._metalnessUniform;

    const wave = TSL.sin(
      TSL.positionLocal.x.mul(this._frequencyUniform).add(TSL.time.mul(this._speedUniform))
    ).mul(this._amplitudeUniform);
    const displaced = TSL.positionLocal.add(TSL.normalWorld.mul(wave));
    this.positionNode = displaced;
    const brightness = wave.mul(TSL.float(3)).add(TSL.float(0.5));
    this.colorNode = TSL.vec3(brightness.mul(TSL.float(0.4)), brightness.mul(TSL.float(0.7)), brightness);
  }

  public serialize(dumper: IDumper): void {
    this.serializeAuthoredFields(dumper);
  }

  public static staticDeserialize(data: unknown, loader: ILoader): VertexWaveNodeMaterialAsset {
    const instance = new VertexWaveNodeMaterialAsset();
    VertexWaveNodeMaterialAsset.loadAuthoredFields(instance, loader);
    instance.rebuild();
    return instance;
  }
}
